import React from "react";
import { Box, Typography, useTheme } from "@mui/material";
import { alpha } from "@mui/material/styles";
import RemoveIcon from "@mui/icons-material/Remove";
import CheckIcon from "@mui/icons-material/Check";

export type ShareboxStep = {
  title: string;
  subtitle: string;
  status: "active" | "collapsed" | "completed";
  actionLabel?: string;
};

type Props = {
  steps: ShareboxStep[];
  onStepAction?: (index: number) => void;
};

export default function ShareboxStepList({ steps, onStepAction }: Props) {
  const theme = useTheme();
  const accent = theme.palette.primary.main;
  const doneColor = theme.palette.success.main;

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
      {steps.map((step, index) => {
        const isActive = step.status === "active";
        const isCompleted = step.status === "completed";
        return (
          <Box
            key={step.title}
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1.5,
              p: 2,
              borderRadius: 3,
              border: "1px solid",
              borderColor: isActive ? accent : "grey.300",
              backgroundColor: isActive
                ? alpha(accent, 0.08)
                : "background.paper",
            }}
          >
            <Box
              sx={{
                width: 32,
                height: 32,
                borderRadius: "50%",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                color: "#fff",
                fontWeight: 700,
                flexShrink: 0,
                backgroundColor: isCompleted
                  ? doneColor
                  : isActive
                  ? accent
                  : "grey.500",
              }}
            >
              {isCompleted ? <CheckIcon fontSize="small" /> : index + 1}
            </Box>
            <Box sx={{ flexGrow: 1, minWidth: 0 }}>
              <Typography sx={{ fontWeight: 700 }}>{step.title}</Typography>
              <Typography variant="body2" color="text.secondary">
                {step.subtitle}
              </Typography>
            </Box>
            {isActive && step.actionLabel ? (
              <Typography
                component="button"
                type="button"
                onClick={() => onStepAction?.(index)}
                sx={{
                  fontWeight: 700,
                  color: accent,
                  whiteSpace: "nowrap",
                  cursor: "pointer",
                  background: "none",
                  border: "none",
                  p: 0,
                  font: "inherit",
                }}
              >
                {step.actionLabel} →
              </Typography>
            ) : isCompleted ? (
              <CheckIcon fontSize="small" sx={{ color: doneColor }} />
            ) : (
              <RemoveIcon fontSize="small" sx={{ color: "grey.400" }} />
            )}
          </Box>
        );
      })}
    </Box>
  );
}
